<script lang="ts">
	import {
		Text,
		Select,
		ControlsContainer,
		Stack,
		ButtonGroup,
		Button,
	} from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import {
		getHoursOptions,
		getMinOptions,
	} from '../../../../../../../DevicesSettings/Schedules/SchedulesHelpers/schedules';
	import { is_sunrise_sunset_condition_ui } from '../../../../../../../../types/SunriseSunsetConditionUI';
	import { WHEN_WIZARD_STEPS_INDEXES } from '../../helpers/when-wizard-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let sceneData: any;

	let hours: number = 0;
	let mins: number = 0;
	let activeButton = 'after';
	let activeMode = 'sunrise';
	let minsOptions = getMinOptions(0, 59);
	let hoursOptions = getHoursOptions(0, 12);

	const activeButtonAndModeTranslationMap = {
		after: $format('text_after', { default: 'after' }),
		before: $format('text_before', { default: 'before' }),
		sunrise: $format('schedule_opt_sunrise', {
			default: 'sunrise',
		}).toLocaleLowerCase(),
		sunset: $format('schedule_opt_sunset', {
			default: 'sunset',
		}).toLocaleLowerCase(),
	};

	if (is_sunrise_sunset_condition_ui(sceneData.translated)) {
		sceneData._sunrise_sunset_cond = { ...sceneData.translated };

		hours = sceneData._sunrise_sunset_cond.hours;
		mins = sceneData._sunrise_sunset_cond.mins;
		activeButton = sceneData._sunrise_sunset_cond.beforeAfter;
		activeMode = sceneData._sunrise_sunset_cond.type;
	}

	const createTimeData = (e: CustomEvent, type: 'hour' | 'min' = 'hour') => {
		const selectedValue: SelectOptionPropsType = e.detail;
		const currentHours: number = type === 'hour' ? Number(selectedValue.value) : hours;
		const currentMins: number = type === 'min' ? Number(selectedValue.value) : mins;
		hours = currentHours;
		mins = currentMins;
		sceneData._sunrise_sunset_cond.mins = mins;
		sceneData._sunrise_sunset_cond.hours = hours;
	};

	const change_active_mode = (mode: 'sunrise' | 'sunset') => {
		sceneData._sunrise_sunset_cond.type = mode;
		activeMode = sceneData._sunrise_sunset_cond.type;
	};
	const change_active_btn = (val: 'before' | 'after') => {
		sceneData._sunrise_sunset_cond.beforeAfter = val;
		activeButton = sceneData._sunrise_sunset_cond.beforeAfter;
	};

	const validateWizardStepTime = () => {
		sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.TIME_STEP;

		sceneData.isValid =
			sceneData._sunrise_sunset_cond.hours >= 0 &&
			sceneData._sunrise_sunset_cond.hours <= 12 &&
			sceneData._sunrise_sunset_cond.mins >= 0 &&
			sceneData._sunrise_sunset_cond.mins <= 59;
	};
	$: (sceneData, validateWizardStepTime());
</script>

<Text element="h2" class="mb-s">
	{$format('label_choose_time', { default: 'Choose time' })}
</Text>

<ControlsContainer size="m" color={$showNewLayout ? 'color-1' : undefined}>
	<div slot="first">
		<div class="d-flex mb-m justify-content-center">
			<Select
				aria-labelledby="select-3s4xd5z"
				class="width-md-m width-sm-full mr-s"
				icon="fas fa-clock"
				options={hoursOptions}
				value={hoursOptions.find((o) => o.value === hours)}
				on:change={(e) => {
					console.log('e ', e.detail);
					createTimeData(e, 'hour');
				}}
				id="select-3s4xd5z"
			/>
			<Select
				aria-label={$format('select_units_minutes', { default: 'Select minutes' })}
				class="width-md-m width-sm-full"
				icon="fas fa-clock"
				options={minsOptions}
				value={minsOptions.find((o) => o.value === mins)}
				on:change={(e) => {
					console.log('e ', e.detail);
					createTimeData(e, 'min');
				}}
				id="select-smz0upo"
			/>
		</div>
		<hr class="mt-m mb-m" />
		<Stack gap="s" direction="column" class="mb-m">
			<ButtonGroup
				id="buttongroup-7urs1bj"
				aria-label={$format('label_before_after', { default: 'Before/After' })}
			>
				<Button
					class={classMap({
						active: activeButton === 'before',
						'text-capitalize': true,
						'width-sm-full': true,
						'width-md-auto': true,
						'flex-1': true,
					})}
					size="m"
					on:click={(e) => change_active_btn('before')}
					>{$format('text_before', { default: 'before' })}</Button
				>
				<Button
					class={classMap({
						active: activeButton === 'after',
						'text-capitalize': true,
						'width-sm-full': true,
						'width-md-auto': true,
						'flex-1': true,
					})}
					size="m"
					on:click={(e) => change_active_btn('after')}
					>{$format('text_after', { default: 'after' })}</Button
				>
			</ButtonGroup>

			<ButtonGroup
				id="buttongroup-j3qmb1m"
				aria-label={$format('label_sunrise_sunset', { default: 'Sunrise/Sunset' })}
			>
				<Button
					class={classMap({
						active: activeMode === 'sunrise',
						'width-sm-full': true,
						'width-md-auto': true,
						'flex-1': true,
					})}
					size="m"
					on:click={() => change_active_mode('sunrise')}
					>{$format('schedule_opt_sunrise')}</Button
				>
				<Button
					class={classMap({
						active: activeMode === 'sunset',
						'width-sm-full': true,
						'width-md-auto': true,
						'flex-1': true,
					})}
					size="m"
					on:click={() => change_active_mode('sunset')}
					>{$format('schedule_opt_sunset')}</Button
				>
			</ButtonGroup>
		</Stack>

		<!-- // TODO Add translation -->
		<Text styleType="body-small">
			{$format('text_execute_scene_sunrise_sunset')}
			<span class="text-primary">{hours}</span>
			{$format('select_units_hours').toLocaleLowerCase()}
			<span class="text-primary">{mins}</span>
			{$format('select_units_minutes')}
			<span class="text-primary">
				{activeButtonAndModeTranslationMap[activeButton]}
				{activeButtonAndModeTranslationMap[activeMode]}
			</span>
		</Text>
	</div>
</ControlsContainer>
